import { createFileRoute, Link as RouterLink } from "@tanstack/react-router";
import {
  Typography,
  Stack,
  Skeleton,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Breadcrumbs,
  Link as MuiLink,
  Paper,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Folder as FolderIcon,
  NavigateNext,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  useListDocument as useGetDocuments,
  useListDocumentParents,
} from "../../../../api/documentos/endpoints";
import { useFindFolder as useGetFolder } from "../../../../api/documentos/endpoints";
import { useAuth } from "../../../../auth";
import { FolderDocumentList } from "../components/FolderDocumentList";
import { useFolderCache } from "../../../../context/FolderCacheContext";
import {
  FolderContextMenu,
  type ContextMenuNode,
} from "../../../../components/FolderContextMenu";

interface ContextMenuState {
  mouseX: number;
  mouseY: number;
  node: ContextMenuNode;
}

export const Route = createFileRoute("/__auth/documents/folders/$folderId")({
  component: FolderContent,
});

function FolderContent() {
  const { folderId } = Route.useParams();
  return <FolderContentInner key={folderId} />;
}

function FolderContentInner() {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const { folderId } = Route.useParams();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant || "default-domain";
  const { getFolderFromCache } = useFolderCache();

  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

  const cachedFolder = getFolderFromCache(Number(folderId));

  const { data: folderData, isLoading: isFolderLoading } = useGetFolder(
    tenantId,
    Number(folderId),
    {
      query: {
        enabled: !!folderId,
      },
    },
  );

  const { data: parentsData, isLoading: isParentsLoading } =
    useListDocumentParents(tenantId, Number(folderId), {
      query: {
        enabled: !!folderId,
      },
    });

  const { data: subFoldersData, isLoading: isSubFoldersLoading } =
    useGetDocuments(
      tenantId,
      // @ts-ignore - Flat params workaround
      {
        parentId: Number(folderId),
        onlyFolder: true,
        size: 100,
        sort: ["name,asc"],
      } as any,
      {
        query: {
          enabled: !!folderId,
        },
      },
    );

  const [showDeleted, setShowDeleted] = useState(false);

  const { data: documentsData, isLoading: isDocumentsLoading } =
    useGetDocuments(
      tenantId,
      // @ts-ignore - Flat params workaround
      {
        parentId: Number(folderId),
        onlyDocuments: true,
        size: 100,
        sort: ["id,desc"],
      } as any,
      {
        query: {
          enabled: !!folderId,
        },
      },
    );

  const allDocuments =
    (documentsData &&
      (Array.isArray(documentsData)
        ? documentsData
        : (documentsData as any)?.content)) ||
    [];

  const documents = showDeleted
    ? allDocuments
    : allDocuments.filter((doc: any) => doc.situation?.id !== 3);
  const subFolders =
    (subFoldersData &&
      (Array.isArray(subFoldersData)
        ? subFoldersData
        : (subFoldersData as any)?.content)) ||
    [];
  const folder =
    ((folderData as any)?.status === 200 && (folderData as any).data) ||
    folderData;
  const folderName = cachedFolder?.name || folder?.name;
  const parents = (
    (parentsData as any)?.status === 200 &&
    Array.isArray((parentsData as any).data)
      ? (parentsData as any).data
      : Array.isArray(parentsData)
        ? parentsData
        : []
  ) as any[];

  const isTitleLoading = !cachedFolder && isFolderLoading;

  const buildNodeForCurrentFolder = useCallback((): ContextMenuNode => {
    const isWorkspace = folder?.documentType?.name === "Workspace";
    return {
      id: Number(folderId),
      name: folderName || `Pasta ${folderId}`,
      level: isWorkspace ? 0 : 1,
      rootId: Number(folderId),
      workspaceId: folder?.workspace?.id,
      actualWorkspaceId: folder?.workspace?.id,
    };
  }, [folderId, folderName, folder]);

  const buildNodeForSubFolder = useCallback(
    (subfolder: any): ContextMenuNode => {
      return {
        id: subfolder.id,
        name: subfolder.name,
        level: 2,
        rootId: Number(folderId),
        workspaceId: subfolder.workspace?.id || folder?.workspace?.id,
        actualWorkspaceId: subfolder.workspace?.id || folder?.workspace?.id,
        disableCreate: true,
      };
    },
    [folderId, folder],
  );

  const handleOpenContextMenu = useCallback(
    (e: React.MouseEvent, node: ContextMenuNode) => {
      e.preventDefault();
      e.stopPropagation();
      setContextMenu({
        mouseX: e.clientX - 2,
        mouseY: e.clientY - 4,
        node,
      });
    },
    [],
  );

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleFolderClick = (id: number) => {
    navigate({
      to: "/documents/folders/$folderId",
      params: { folderId: id.toString() },
    });
  };

  return (
    <Stack spacing={4} p={3}>
      {isParentsLoading ? (
        <Skeleton width="50%" height={24} />
      ) : (
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" />}
          aria-label="breadcrumb"
        >
          <MuiLink
            component={RouterLink as any}
            to="/documents"
            color="inherit"
            underline="hover"
          >
            {t("documents.home")}
          </MuiLink>
          {folder?.workspace &&
            String(folder.workspace.id) !== String(folderId) && (
              <MuiLink
                component={RouterLink as any}
                to="/documents/folders/$folderId"
                params={{ folderId: folder.workspace.id.toString() }}
                color="inherit"
                underline="hover"
                // @ts-ignore
                preload={false}
              >
                {folder.workspace.name}
              </MuiLink>
            )}
          {parents
            .filter((p: any) => {
              if (String(p.id) === String(folderId)) return false;
              if (folder?.workspace) {
                if (String(p.id) === String(folder.workspace.id)) return false;
                if (p.name && p.name === folder.workspace.name) return false;
              }
              return true;
            })
            .map((parent: any) => (
              <MuiLink
                key={parent.id}
                component={RouterLink as any}
                to="/documents/folders/$folderId"
                params={{ folderId: parent.id.toString() }}
                color="inherit"
                underline="hover"
                // @ts-ignore
                preload={false}
              >
                {parent.name}
              </MuiLink>
            ))}
          <Typography color="text.primary">
            {folderName || `Pasta ${folderId}`}
          </Typography>
        </Breadcrumbs>
      )}

      <Stack direction="row" alignItems="center" spacing={1}>
        <FolderIcon color="primary" fontSize="large" />
        {isTitleLoading ? (
          <Skeleton width={300} height={60} animation="wave" />
        ) : (
          <Typography variant="h4">
            {folderName || `Pasta ${folderId}`}
          </Typography>
        )}
        {!isTitleLoading && (
          <IconButton
            size="small"
            onClick={(e) =>
              handleOpenContextMenu(e, buildNodeForCurrentFolder())
            }
            sx={{ ml: 0.5 }}
          >
            <MoreVertIcon />
          </IconButton>
        )}
      </Stack>

      <Box>
        <Typography
          variant="h6"
          gutterBottom
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          {t("documents.subfolders", "Pastas")}
        </Typography>
        {isSubFoldersLoading ? (
          <Stack spacing={1}>
            <Skeleton
              height={50}
              variant="rectangular"
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              height={50}
              variant="rectangular"
              sx={{ borderRadius: 1 }}
            />
          </Stack>
        ) : subFolders.length > 0 ? (
          <List
            disablePadding
            sx={{
              bgcolor: "background.paper",
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
            }}
          >
            {subFolders.map((subfolder: any, index: number) => (
              <Box key={subfolder.id}>
                <ListItemButton
                  onClick={() => handleFolderClick(subfolder.id)}
                  onContextMenu={(e) =>
                    handleOpenContextMenu(e, buildNodeForSubFolder(subfolder))
                  }
                >
                  <ListItemIcon>
                    <FolderIcon color="action" />
                  </ListItemIcon>
                  <ListItemText primary={subfolder.name} />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenContextMenu(
                        e,
                        buildNodeForSubFolder(subfolder),
                      );
                    }}
                    sx={{ opacity: 0.5, "&:hover": { opacity: 1 } }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
                {index < subFolders.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        ) : (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography color="textSecondary">
              {t("documents.noSubfoldersFound", "Nenhuma subpasta encontrada.")}
            </Typography>
          </Paper>
        )}
      </Box>

      <Box sx={{ mb: 10 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            {t("documents.title", "Documentos")}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                {t("documents.showDeleted", "Mostrar excluídos")}
              </Typography>
            }
          />
        </Stack>
        <FolderDocumentList
          documents={documents}
          isLoading={isDocumentsLoading}
          tenantId={tenantId}
        />
      </Box>

      <FolderContextMenu
        contextMenu={contextMenu}
        onClose={handleCloseContextMenu}
        tenantId={tenantId}
      />
    </Stack>
  );
}
